import { Banknote, Download, Search } from 'lucide-react'

import { getHighestRole, isPrivilegedRole } from '@ankor-io/common/domain/iam/account'
import { Role } from '@ankor-io/common/types/account'
import { CharterStatus, type CharterSummary } from '@ankor-io/common/types/charter'
import type { Transaction } from '@ankor-io/common/types/transaction/transaction'

import { useUserContext } from '@/providers/user/useUserContext'
import { Button } from '@/ui-components/Button'
import { Heading } from '@/ui-components/Heading'
import CharterTransactionsTable from '@/views/CharterDetail/CharterTransactionsTable'
import TransactionFiltersComponent, { type TransactionFilters } from '@/views/components/TransactionFilters'

type TransactionsProps = {
  charter: CharterSummary
  transactions: Transaction[]
  transactionFilters: TransactionFilters
  setTransactionFilters: (filters: TransactionFilters) => void
  onUpdatedTransactions: (transactions: Transaction[]) => void
}

const Transactions = ({
  charter,
  transactions,
  transactionFilters,
  setTransactionFilters,
  onUpdatedTransactions,
}: TransactionsProps) => {
  const { activeOrganizationRole } = useUserContext()

  return (
    <div className="flex flex-col gap-y-4 rounded-lg p-4 sm:p-6 border border-gray-200 dark:border-gray-700">
      <div className="flex md:items-center justify-between flex-col md:flex-row gap-4">
        <Heading level={3}>Expenses</Heading>
        <div className="flex gap-2 w-full md:w-auto">
          <Button
            outline
            className="w-full md:w-auto"
            onClick={() => {
              console.log('Export')
            }}
          >
            <Download data-slot="icon" />
            Export
          </Button>
          {charter.status !== CharterStatus.COMPLETED &&
            charter.status !== CharterStatus.CANCELLED &&
            (charter.status !== CharterStatus.SIGNING_OFF || isPrivilegedRole(activeOrganizationRole)) &&
            getHighestRole([activeOrganizationRole]) !== Role.VIEWER && (
              <Button
                color="primary"
                className="w-full md:w-auto"
                to={`/charters/${charter.id}/transactions/new`}
                data-testid="log-expense-button-header"
              >
                Log Expense
              </Button>
            )}
        </div>
      </div>

      <TransactionFiltersComponent
        transactionFilters={transactionFilters}
        setTransactionFilters={setTransactionFilters}
      />

      <CharterTransactionsTable
        transactions={transactions}
        onUpdatedTransactions={onUpdatedTransactions}
      />

      {/* Empty State */}
      {transactions.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 px-4">
          <div className="text-center">
            {!transactionFilters.dateFrom &&
            !transactionFilters.dateTo &&
            !transactionFilters.status &&
            !transactionFilters.categoryId &&
            !transactionFilters.type ? (
              // No transactions at all
              <div>
                <Banknote className="mx-auto size-12 text-gray-400 dark:text-gray-500 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No expenses yet</h3>

                {charter.status !== CharterStatus.COMPLETED &&
                  charter.status !== CharterStatus.CANCELLED &&
                  (charter.status !== CharterStatus.SIGNING_OFF || isPrivilegedRole(activeOrganizationRole)) && (
                    <>
                      <p className="text-gray-500 dark:text-gray-400 mb-4 max-w-sm">
                        Get started by logging your first expense. It will appear here once submitted.
                      </p>
                      <Button
                        color="primary"
                        className="w-full md:w-auto"
                        to={`/charters/${charter.id}/transactions/new`}
                        data-testid="log-expense-button-empty-state"
                      >
                        Log Expense
                      </Button>
                    </>
                  )}
              </div>
            ) : (
              // Filters applied but no results
              <div>
                <Search className="mx-auto size-12 text-gray-400 dark:text-gray-500 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No expenses found</h3>
                <p className="text-gray-500 dark:text-gray-400 mb-4 max-w-sm">
                  Try adjusting your search or filter criteria to find what you're looking for.
                </p>
                <Button
                  outline
                  onClick={() => {
                    setTransactionFilters({
                      dateFrom: '',
                      dateTo: '',
                      status: '',
                      categoryId: '',
                      type: '',
                    })
                  }}
                >
                  Clear Filters
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default Transactions
